import { fileStructure } from "./FileStructure";


export const formatForRoute = (str: string) => {
    return str
        .replace(/ *: */g, ' ') // replace colon with space
        .replace(/ *- */g, ' ') // replace dash with space
        .split(' ')
        .join('-')
        .toLowerCase();
};

export const removeDashes = (str: string) => {
    return str.split('-').join(' ');
};

export const capitalizeFirstLetter = (str: string) => {
    const split = str.split(' ');
    return split.map((word) => word && `${word[0].toUpperCase()}${word.substring(1).toLowerCase()}`).join(' ');
};


export const formatRouteText = (route: string | undefined) => {
    if (route) {
        return (capitalizeFirstLetter(removeDashes(route)));
    }
    return "";
};


export const formatTitleForBottomNav = (title: string) => {
    if (title.includes('/')) {
        title = title.split('/').join(': ');
    }
    return title;
};

export const formatBreadcrumbLabel = (label: any) => {
    // Add space between words using regex, and add spaces around hyphens
    return label
        .replace(/([a-z&])([A-Z])/g, '$1 $2')
        .replace(/&/g, ' & ')
        .replace(/ *- */g, ' - '); // Add spaces around hyphens
};

export const formatTitle = (title: string) => {
    if (title.includes("/")) {
        title = title.split("/").join(": ");
    }
    return title;
}

// Handle the special formatting in the appendix where additional sections from the main report are kept
export const handleAppendixSubsections = (currentSubsection: string, deliminator?: string) => {
    const splitCurrentSubsection = currentSubsection.split("-");
    if (splitCurrentSubsection.slice(0, 3).join("-") === "the-analytic-sample") {
        return (
            `${splitCurrentSubsection.slice(0, 3).join("-")}-${splitCurrentSubsection.slice(3).join("-")}`
        );
    }
    if (splitCurrentSubsection.slice(0, 3).join("-") === "the-negotiation-terms") {
        return (
            `${splitCurrentSubsection.slice(0, 3).join("-")}-${splitCurrentSubsection.slice(3).join("-")}`
        );
    }
    return (currentSubsection);
}

export const getNextSection = (currentSection: string, currentSubsection: string) => {
    if (currentSection === "about-this-report") {
        return "the-analytic-sample/introduction";
    }

    if (currentSection === "appendix") {
        currentSubsection = handleAppendixSubsections(currentSubsection);
    }

    let currentSectionIndex = 0;
    let currentSubsectionIndex = 0;
    let next = { url: "" };

    const section = fileStructure.find((s, i) => {
        if (s.url === currentSection) {
            currentSectionIndex = i;
        }
        return s.url === currentSection;
    });

    if (section) {
        (section.subsections.find((subsection, i) => {
            // handle third level subsections
            if (subsection.subsections.length > 0) {
                // if current subsection is a subsubsection
                if (currentSubsection === subsection.subsections[0].url) {
                    currentSubsectionIndex = i;
                    next = section.subsections[i + 1];
                    return currentSubsection === subsection.subsections[0].url;
                } else {
                    // current subsection has subsections, so NEXT is a subsubsection
                    next = subsection.subsections[0];
                    return subsection.url === currentSubsection;
                }
                // for regular subsections
            } else if (subsection.url === currentSubsection) {
                currentSubsectionIndex = i;
                next = section.subsections[i + 1];
                return subsection.url === currentSubsection;
            }
        }))

        // if currentSubsectionIndex is at last position, go to the next section and return the first subsection
        if (currentSubsectionIndex === section.subsections.length - 1) {
            let newSection = fileStructure[currentSectionIndex + 1];
            return `${newSection.url}/${newSection.subsections[0].url}`;
        }

        if (next) {
            return `${section.url}/${next.url}`;
        }
    }

    return "";
}

export const getPreviousSection = (currentSection: string, currentSubsection: string) => {

    if (currentSection === "the-analytic-sample" && currentSubsection === "introduction") {
        return "about-this-report";
    }

    if (currentSection === "appendix") {
        currentSubsection = handleAppendixSubsections(currentSubsection);
    }

    let currentSectionIndex = 0;
    let currentSubsectionIndex = 0;
    let prev = { url: "" };

    const section = fileStructure.find((s, i) => {
        if (s.url === currentSection) {
            currentSectionIndex = i;
        }
        return s.url === currentSection;
    });

    if (section) {
        (section.subsections.find((subsection, i) => {
            if (subsection.url === currentSubsection) {
                // handle third level subsections
                // the previous subsection is a subsubsection
                if (i > 1 && section.subsections[i - 1].subsections.length > 0) {
                    currentSubsectionIndex = i;
                    prev = section.subsections[i - 1].subsections[0];
                    return subsection.url === prev.url;
                } else {
                    // for regular subsections
                    currentSubsectionIndex = i;
                    prev = section.subsections[i - 1];
                    return subsection.url === currentSubsection;
                }
            }
            if (subsection.subsections.length > 0) {
                // if current subsection is a subsubsection
                if (currentSubsection === subsection.subsections[0].url) {
                    currentSubsectionIndex = i;
                    prev = section.subsections[i];
                    return subsection.url === currentSubsection;
                } 
            }
        }))

        // if subsectionIndex is at first position, go to the previous section and return the last subsection
        if (currentSubsectionIndex === 0) {
            let newSection = fileStructure[currentSectionIndex - 1];

            // handle when previous section is a subsubsection
            if (newSection.subsections[newSection.subsections.length - 1].subsections.length > 0) {
                return `${newSection.url}/${newSection.subsections[newSection.subsections.length - 1].subsections[0].url}`;
            }
            return `${newSection.url}/${newSection.subsections[newSection.subsections.length - 1].url}`;
        }

        if (prev) {
            return `${section.url}/${prev.url}`;
        }
    }
    return "";
}

export const getFirstSection = () => {
    return fileStructure[0].url;
}

export const getLastSection = (): string | null => {
    if (fileStructure.length === 0) return null;

    let current = fileStructure[fileStructure.length - 1];

    while (current.subsections.length > 0) {
        current = current.subsections[current.subsections.length - 1];
    }

    return current.url;
}

export const getFirstSubsection = (title: string) => {
    const formattedSection = formatRouteText(title);
    const section = fileStructure.find((s) => s.title === formattedSection);

    if (section && section.title === "About This Report") {
        return "";
    }
    if (!section || section.subsections.length === 0) {
        return "introduction";
    }

    return section.subsections[0].url;
}